import React from 'react'
import { View } from 'react-native'
import styled from 'styled-components/native'
import { useSelector } from 'APP/Hooks/redux'
import I18n, { isTranslationKey } from 'APP/I18n'
import Images from 'APP/Themes/Images'
import { useTheme } from 'styled-components/native'

import ServiceDetailCard from 'APP/Components/ServiceDetailCard'
import { determineServiceType } from './utils'
import Typography from 'APP/Converse/Typography'

interface IListItem {
  id: string
  icon: keyof typeof Images
  text: string | undefined
}

interface SectionInfo {
  id: string
  title: string | undefined
  content: IListItem[]
}

const Container = styled(View)`
  margin-horizontal: ${({ theme }) => theme.metrics.baseMargin * 2}px;
  margin-bottom: ${({ theme }) => theme.metrics.baseMargin * 2}px;
  gap: 20px;
`

const CardFooterContainer = styled(View)`
  align-items: center;
`

const SectionsContainer = styled(View)`
  margin-horizontal: ${({ theme }) => theme.metrics.baseMargin * 2}px;
`

const Section = styled(View)<{ isLast?: boolean }>`
  padding-bottom: ${({ theme }) => theme.metrics.baseMargin * 2}px;
  border-bottom-width: ${({ isLast }) => (isLast ? '0px' : '1px')};
  border-bottom-color: ${({ theme }) => theme.colors.separateLine};
  margin-bottom: ${({ theme, isLast }) => (isLast ? '0px' : `${theme.metrics.baseMargin * 2}px`)};
`

const SectionTitle = styled(Typography)`
  margin-bottom: ${({ theme }) => theme.metrics.baseMargin * 1.5}px;
`

const List = styled(View)`
  gap: ${({ theme }) => theme.metrics.baseMargin * 1.5}px;
`

const ListItem = styled(View)`
  flex-direction: row;
  align-items: center;
  flex-wrap: wrap;
`

const IconContainer = styled(View)`
  width: 40px;
  height: 40px;
  border-radius: 20px;
  justify-content: center;
  align-items: center;
  margin-right: ${({ theme }) => theme.metrics.baseMargin}px;
  background-color: ${({ theme }) => theme.colors.serviceDetailCardBg};
  display: flex;
`

const ListText = styled(Typography)`
  line-height: 22px;
  flex: 1;
  flex-shrink: 1;
`

const translate = (key: string, options?: { value: number | null }) =>
  isTranslationKey(key) ? I18n.t(key, options) : undefined

export const ServiceDetailContainer = ({ service = 'nutrition' }) => {
  const theme = useTheme()
  const services = useSelector((state) => state.patient.profile.eligibleServices)

  const { type, value } = determineServiceType(services, 'nutrition_consults')

  const primaryItem = {
    header: translate(`serviceDetails.${service}.${type}.primary.header`),
    body: translate(`serviceDetails.${service}.${type}.primary.body`, { value }),
    footer: translate(`serviceDetails.${service}.${type}.primary.footer`),
  }

  const secondaryItem = {
    header: translate(`serviceDetails.${service}.${type}.secondary.header`),
    body: translate(`serviceDetails.${service}.${type}.secondary.body`, { value }),
    footer: translate(`serviceDetails.${service}.${type}.secondary.footer`),
  }

  const infos: SectionInfo[] = [
    {
      id: 'inclusions',
      title: translate(`serviceDetails.${service}.inclusionsAndExclusions.inclusions.title`),
      content: [
        {
          id: 'personalized',
          text: translate(
            `serviceDetails.${service}.inclusionsAndExclusions.inclusions.content.0.text`
          ),
          icon: translate(
            `serviceDetails.${service}.inclusionsAndExclusions.inclusions.content.0.icon`
          ) as keyof typeof Images,
        },
        {
          id: 'guidance',
          text: translate(
            `serviceDetails.${service}.inclusionsAndExclusions.inclusions.content.1.text`
          ),
          icon: translate(
            `serviceDetails.${service}.inclusionsAndExclusions.inclusions.content.1.icon`
          ) as keyof typeof Images,
        },
        {
          id: 'support',
          text: translate(
            `serviceDetails.${service}.inclusionsAndExclusions.inclusions.content.2.text`
          ),
          icon: translate(
            `serviceDetails.${service}.inclusionsAndExclusions.inclusions.content.2.icon`
          ) as keyof typeof Images,
        },
      ],
    },
    {
      id: 'exclusions',
      title: translate(`serviceDetails.${service}.inclusionsAndExclusions.exclusions.title`),
      content: [
        {
          id: 'prescription',
          text: translate(
            `serviceDetails.${service}.inclusionsAndExclusions.exclusions.content.0.text`
          ),
          icon: translate(
            `serviceDetails.${service}.inclusionsAndExclusions.exclusions.content.0.icon`
          ) as keyof typeof Images,
        },
        {
          id: 'analysis',
          text: translate(
            `serviceDetails.${service}.inclusionsAndExclusions.exclusions.content.1.text`
          ),
          icon: translate(
            `serviceDetails.${service}.inclusionsAndExclusions.exclusions.content.1.icon`
          ) as keyof typeof Images,
        },
      ],
    },
  ]

  return (
    <>
      <Container>
        <ServiceDetailCard item={primaryItem} accent={type !== 'outOfPocket'} />
        <ServiceDetailCard item={secondaryItem} />
        <CardFooterContainer>
          <Typography variant="caption" color="text">
            {translate(`serviceDetails.${service}.cardFooter`)}
          </Typography>
        </CardFooterContainer>
      </Container>
      <SectionsContainer>
        {infos.map((info, sectionIndex) => (
          <Section key={info.id} isLast={sectionIndex === infos.length - 1}>
            <SectionTitle variant="h2">{info.title}</SectionTitle>
            <List>
              {info.content.map((item) => {
                const icon = Images[item.icon]
                return (
                  <ListItem key={item.id}>
                    <IconContainer>
                      {icon &&
                        React.createElement(icon, {
                          fillLine: theme.colors.serviceIconPrimary,
                          accentFillLine: theme.colors.serviceIconAccent,
                        })}
                    </IconContainer>
                    <ListText variant="bodyLarge" color="textSecondary">
                      {item.text}
                    </ListText>
                  </ListItem>
                )
              })}
            </List>
          </Section>
        ))}
      </SectionsContainer>
    </>
  )
}
